/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../root'
import { useModal } from '../../contexts/ModalContext'
import Modal from '../Modal'
import LoadingSpinner from '../LoadingSpinner'
import TextLinkButton from '../shared/TextLinkButton'
import ParticipatingFsisModal from './ParticipatingFsisModal'
import WfService from '../../lib/WfService'
import { WorkflowType } from '../../constants/workflow'

interface WorkflowCancellationCommentModalProps {
  workflow: any
  onClose?: () => void
  commentModalClass?: {
    commentsModalClass?: string
    commentsBackdropClass?: string
  }
}

interface WorkflowDetails {
  id: number
  workflowNo?: string
  workflowType?: string
  files?: Array<any>
  filename?: string
  cancellationComment?: string
}

/**
 * Workflow Cancellation Comment Modal Component
 *
 * Displays the cancellation comment for a cancelled workflow:
 * - Workflow number (for EOS workflows)
 * - Document information (for non-EOS workflows)
 * - Cancellation comment text
 *
 * Based on lego/page/workflows/details/ViewCancellationCommentModal.jsx
 */
export const WorkflowCancellationCommentModal: React.FC<WorkflowCancellationCommentModalProps> = ({
  workflow,
  onClose,
  commentModalClass
}) => {
  const { t } = useTranslation()
  const context = useServiceContext()
  const { showModal } = useModal()
  const [loading, setLoading] = useState(false)
  const [workflowDetails, setWorkflowDetails] = useState<WorkflowDetails | null>(null)
  const [isEosWf, setIsEosWf] = useState(false)
  const [workingFile, setWorkingFile] = useState<any>({})

  useEffect(() => {
    if (!workflow || !workflow.id) {
      console.error('No Workflow ID specified')
      return
    }

    setLoading(true)

    const loadAndSetComments = (wfData: any) => {
      setWorkflowDetails(wfData)
      setIsEosWf(WorkflowType.EOS_DOWNLOAD_AND_ACKNOWLEDGE === wfData.workflowType)
      setWorkingFile(WfService.getWorkingFileFromWF(wfData) || {})

      // Load workflow comments (even though not displayed - legacy behavior)
      context.api.workflows
        .getWorkflowComments(workflow.id)
        .then(() => {
          setLoading(false)
        })
        .catch(() => {
          console.error('Comments could not be loaded')
          setLoading(false)
        })
    }

    // If workflow already has workflowType, use it directly
    if (workflow.workflowType) {
      loadAndSetComments(workflow)
    } else {
      // Otherwise fetch full workflow details
      context.api.workflows
        .getWorkflow(workflow.id)
        .then((wfData) => {
          loadAndSetComments(wfData)
        })
        .catch(() => {
          console.error('WF ' + workflow.id + ' not loaded')
          setLoading(false)
        })
    }
  }, [workflow, context.api])

  const handleClose = () => {
    // Only call onClose - ModalContainer will handle closing this specific modal
    // Calling hideModal() without ID would close ALL modals
    if (onClose) {
      onClose()
    }
  }

  return (
    <Modal
      show={true}
      handleClose={handleClose}
      title={t('VIEW_CANCELLATION_COMMENT')}
      size="md"
      isConfirmationDialog={true}
      className={commentModalClass?.commentsModalClass}
    >
      <Modal.Body>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <LoadingSpinner size={40} color="#3374c8" />
          </div>
        ) : (
          <>
            {/* Workflow Number (for EOS workflows) */}
            {isEosWf && (
              <div className="form-group wparag">
                <label className="control-label">{t('WORKFLOW_CHANGE_REQUEST_NUMBER')}</label>
                <p className="whitespaceprewrap">{workflow?.workflowNo}</p>
              </div>
            )}

            {/* Document(s) section (for non-EOS workflows) */}
            {!isEosWf && (
              <div className="form-group wparag">
                <label className="control-label mb0">{t('DOCUMENT')}</label>
                <p className="whitespaceprewrap">
                  {workflow?.files && workflow.files.length > 1 && (
                    <TextLinkButton
                      action={() => {
                        showModal(<ParticipatingFsisModal wfId={workflow.id} show={true} onClose={() => {}} />, {
                          keepParentModalsOpen: true
                        })
                      }}
                    >
                      {t('MULTIPLE_DOCS')}
                    </TextLinkButton>
                  )}
                  {workflow?.files && workflow.files.length === 0 && (
                    <span>{workflow.filename || t('NOT_AVAILABLE')}</span>
                  )}
                  {workflow?.files && workflow.files.length === 1 && workflow.id && <span>{workflow.filename}</span>}
                </p>
              </div>
            )}

            {/* Cancellation Comment */}
            <div className="form-group wparag w100p mb0">
              <label className="control-label mb0">{t('CANCELLATION_COMMENT')}</label>
              {!workflow?.cancellationComment?.length && (
                <p className="fs12">{t('NO_CANCELLATION_COMMENT_AVAILABLE')}</p>
              )}
              {workflow?.cancellationComment && <p className="fs12">{workflow.cancellationComment}</p>}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          data-action="ok"
          className="btn btn-primary text-capitalize"
          onClick={handleClose}
          id="be78d793-f56c-4cff-89cc-bb8ff47785e3"
        >
          {t('OK')}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default WorkflowCancellationCommentModal
